import React, { ReactNode, useEffect, useState } from 'react';
import cn from 'classnames';

import withFallback from '../image-with-fallback';
import stripHtml from '../../utils/strip-html';
import {
  isGridLayout,
  Offer,
  popupLayoutCssPostfix,
  Product,
  ProductVariant,
  Settings,
} from '../../types';
import { ProductOfferPricing } from '../ProductOfferPricing';
import {
  Title,
  Input,
  OfferQuantity,
  RemoveButton,
  AnimatedArrow,
  AnimatedCheckmark,
} from '../elements';
import { OfferBody } from '../OfferBody';
import ProductDetail from '../ProductDetail';
import { useProductsContext } from '../../contexts/ProductsContext';
import useAnimation from '../../hooks/useAnimation';
import { BACKEND_MAX_VALUE } from '../elements/OfferQuantity';
import { usePopupContext } from '../../contexts/Settings';
interface Props {
  added: boolean;
  handleOfferQuantityChange: React.Dispatch<React.SetStateAction<number>>;
  handleRemoveFromCartClick: () => Promise<void>;
  loading: boolean;
  offer: Offer;
  offerCount: number;
  indexInOffers?: number;
  product: Product;
  primaryButton: ReactNode;
  quantity: number;
  select: (className: string) => ReactNode | null;
  setOfferNote: (note: string) => void;
  offerNote?: string;
  variant: ProductVariant;
  filteredProductVariantsFromOffer: ProductVariant[];
}

function createOfferImageClasses(settings: Settings): string {
  if (isGridLayout(settings)) {
    return '';
  } else {
    switch (settings.image_size) {
      case 'small':
        return 'offer-image-small';
      case 'medium':
        return 'offer-image-medium';
      case 'large':
      default:
        return 'offer-image-large';
    }
  }
}

export const ProductOfferView = ({
  added,
  handleOfferQuantityChange,
  handleRemoveFromCartClick,
  filteredProductVariantsFromOffer,
  loading,
  offer,
  offerCount,
  indexInOffers,
  primaryButton,
  product,
  quantity,
  select,
  setOfferNote,
  offerNote = '',
  variant,
}: Props) => {
  const AnimatedIcon = offer.is_upgrade ? AnimatedArrow : AnimatedCheckmark;
  const { selectedProduct, setProductDetail } = useProductsContext();
  const canPlayAnimation = useAnimation({ play: added });
  const [currentVariant, setCurrentvariant] = useState(variant);
  const [currentQuantity, setCurrentQuantity] = useState(
    Math.min(quantity, BACKEND_MAX_VALUE),
  );
  const { settings } = usePopupContext();

  function getImageSize(settings: Settings) {
    if (isGridLayout(settings)) {
      return 'large';
    } else if (settings.image_size === 'small') {
      return 'medium';
    } else {
      return settings.image_size;
    }
  }

  useEffect(() => {
    const isChangedVariant = currentVariant
      ? currentVariant.id !== variant.id
      : true;

    if (isChangedVariant) {
      setCurrentvariant(variant);
    }

    const isMatchingVariant = selectedProduct?.variantID
      ? selectedProduct.variantID === variant.id || isChangedVariant
      : true;

    const isQuantityChanged =
      currentQuantity !== quantity && offer.enable_quantity_selector;
    if (isQuantityChanged) {
      setCurrentQuantity(Math.min(quantity, BACKEND_MAX_VALUE));
    }

    if (
      selectedProduct?.productID === product.id &&
      (isMatchingVariant || isChangedVariant || isQuantityChanged)
    ) {
      setProductDetail(
        <ProductDetail
          product={product}
          offer={offer}
          variant={variant}
          buyContainer={
            <>
              {select && select('')}
              {offer.show_note_field && (
                <div className="Note-Field--wrapper">
                  <Input
                    className="md:w-250"
                    disabled={added}
                    defaultValue={offerNote}
                    onChange={(e) => {
                      setOfferNote(e.target.value);
                    }}
                  />
                </div>
              )}
              <div className="flex items-stretch">
                {offer.enable_quantity_selector && (
                  <OfferQuantity
                    quantity={quantity}
                    handleOfferQuantityChange={handleOfferQuantityChange}
                    disabled={added || loading}
                    containerClasses="mr-10"
                    inputClasses="Quantity-Selector-Limit-Width ml-4"
                  />
                )}
                {primaryButton}
                {added ? (
                  <RemoveButton
                    disabled={loading}
                    onClick={handleRemoveFromCartClick}
                    className="Button-Remove"
                  />
                ) : null}
              </div>
            </>
          }
        />,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProduct,
    product,
    added,
    variant,
    quantity,
    loading,
    offer,
    offerNote,
    currentVariant,
  ]);

  return (
    <OfferBody
      id={product.id}
      offerCount={offerCount}
      hidden={!!selectedProduct}
      indexInOffers={indexInOffers}
      offer={offer}
      upsellListItemCustomClassName={`Product-Id-${
        product.id
      }${popupLayoutCssPostfix(settings)}`}
      cyData="offer-product"
      img={
        <img
          src={withFallback(
            (variant && variant.featured_image && variant.featured_image.src) ||
              product.featured_image,
            // Choose image size based on layout and settings.image_size:
            // - For grid layout, use 'large' size
            // - For tree layout:
            //   - If settings.image_size is 'small' or 'medium', use 'medium' size (using small size images decreases image quality)
            //   - If settings.image_size is 'large', use 'large' size
            getImageSize(settings),
          )}
          alt="Product"
          className={cn(
            createOfferImageClasses(settings),
            'md:object-scale-down overflow-y-hidden',
          )}
        />
      }
      animatedIcon={
        added ? (
          <AnimatedIcon size="md" playAnimation={canPlayAnimation} />
        ) : null
      }
      loading={loading}
      added={added}
      select={(className) => select(className)}
      handleRemoveFromCartClick={handleRemoveFromCartClick}
      primaryButton={primaryButton}
      setOfferNote={setOfferNote}
      offerNote={offerNote}
      offerDescriptionHeadingTitle={
        <Title clickable className="Offer-Title">
          <span className="font-normal text-grey-2 text-md">
            {quantity > 1 ? `${quantity} × ` : ''}
          </span>
          {stripHtml(product.title)}
          {/*We need to check the variants of the product we receive from Shopify and also the variants we filter and assign on frontend to the offer */}
          {(filteredProductVariantsFromOffer.length === 1 ||
            product.variants.length === 1) &&
          variant.title !== 'Default Title'
            ? ` - ${variant.title}`
            : null}
        </Title>
      }
      offerDescriptionHeadingPrice={
        <ProductOfferPricing
          offer={offer}
          variant={variant}
          quantity={quantity}
        />
      }
      label={product.vendor}
      quantity={quantity}
      handleOfferQuantityChange={handleOfferQuantityChange}
      variantID={variant.id}
      handle={product.handle}
    />
  );
};
